import { motion } from "framer-motion";
import { Layers, Ruler, Sparkles } from "lucide-react";
import ShopButton from "@/components/mall/ShopButton";
import { BillboardFrame, Bullets, Lines, MoreDetails, Panel, RoomNumber } from "@/components/mall/BillboardKit";

const BLUE = "#1d4ed8";

const SHEETS = [
    {
        label: "Sheet 1 · 3 ft", tall: true,
        blocks: [[86, 58], [52, 58], [120, 34], [40, 40], [40, 40], [40, 40], [66, 46], [98, 30], [46, 30]],
    },
    {
        label: "Sheet 2 · 1 ft", tall: false,
        blocks: [[110, 40], [48, 40], [64, 26], [30, 26], [30, 26]],
    },
];

const PRICES = [
    ["The first foot of the sheet", "$24"],
    ["Each extra foot", "+$5"],
    ["The sheet is always this wide", '15.5"'],
    ["One DTF print on its own", "$5 a piece"],
];

const USE = [
    "You need a few of one design, and a whole sheet for it would be a waste.",
    "You have different designs and want them on the same sheet.",
    "You need names and numbers for a whole team in one run.",
    "You want to restock a few of every design instead of one style.",
];

/** A drawn sheet, nested the way the builder nests it. */
function Sheet({ label, blocks, tall }) {
    return (
        <div className="rounded-2xl border border-zinc-300 bg-white p-3 shadow-[0_18px_40px_-26px_rgba(0,0,0,0.55)]">
            <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                <span>{label}</span>
                <span className="inline-flex items-center gap-1"><Ruler size={9} /> 15.5" wide</span>
            </div>
            <div className="mt-2 flex items-end gap-[2px]">
                {Array.from({ length: 31 }).map((_, i) => (
                    <span key={i} className="w-full border-l border-zinc-300" style={{ height: i % 4 === 0 ? 9 : 5 }} />
                ))}
            </div>
            <div className={`mt-2 flex flex-wrap content-start items-start gap-2 rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-2.5 ${tall ? "h-44" : "h-24"}`}>
                {blocks.map(([w, h], i) => (
                    <span key={i} className="rounded-[5px]"
                        style={{ width: w, height: h, background: i % 3 === 0 ? BLUE : i % 3 === 1 ? "#0B0B0B" : "#a1a1aa", opacity: i % 3 === 1 ? 0.85 : 0.7 }} />
                ))}
            </div>
        </div>
    );
}

/** Room 02 — what a gang sheet is and why it's the cheapest way to print a lot. */
export default function GangSheets() {
    return (
        <BillboardFrame testid="frame-dtf" room="dtf" note="gang sheets">
        <section className="bg-zinc-50" data-testid="mall-room-dtf">
            <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-10 sm:px-6 md:py-12 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        className="space-y-4">
                        {SHEETS.map((s, i) => (
                            <div key={s.label} style={{ transform: `rotate(${i ? 1.4 : -1.4}deg)` }}
                                className="transition-transform duration-500 hover:!rotate-0" data-testid={`gang-sheet-${i}`}>
                                <Sheet {...s} />
                            </div>
                        ))}
                    </motion.div>
                </div>

                <div className="lg:col-span-7">
                    <RoomNumber n="01" room="Shirts · DTF gang sheets" accent={BLUE} className="mt-6" />
                    <h2 className="mt-4 font-display text-4xl font-extrabold leading-[0.94] tracking-tighter text-zinc-900 sm:text-5xl" data-testid="dtf-headline">
                        PUT ALL OF YOUR DESIGNS<br /><span style={{ color: BLUE }}>ON ONE SHEET.</span>
                    </h2>
                    <p className="mt-5 max-w-xl text-base text-zinc-600 md:text-lg" data-testid="dtf-body">
                        A gang sheet is one transfer sheet with many designs on it. We print all of them at
                        once, so you do not pay for each design on its own. The sheet is 15.5 inches wide and can be any
                        length you need. The more designs you fit on the sheet, the less each one costs.
                    </p>

                    <MoreDetails testid="dtf-details" className="mt-7" label="What it costs and when to use it">
                    <div className="grid gap-5 sm:grid-cols-2">
                        <Panel icon={Layers} title="What it costs" accent={BLUE} testid="dtf-pricing">
                            <Lines rows={PRICES} accent="#0B0B0B" />
                            <p className="mt-3 text-xs text-zinc-500">
                                A gang sheet is the cheapest way to print many pieces. A single print is fine when you only need a few.
                            </p>
                        </Panel>
                        <Panel icon={Sparkles} title="When to use it" accent={BLUE} testid="dtf-use">
                            <Bullets items={USE} accent={BLUE} />
                        </Panel>
                    </div>
                    </MoreDetails>

                    <div className="mt-8">
                        <ShopButton room="dtf" tone="blue" testid="room-dtf-cta"
                            title="Which shop prints your gang sheet?"
                            path={(s) => `/shirts/dtf/gang-sheet?store=${s}`}>
                            <Layers size={17} /> Build a gang sheet
                        </ShopButton>
                    </div>
                </div>
            </div>
        </section>
        </BillboardFrame>
    );
}
